/**
 * The browser Effect→OTEL tracer bridge (spec D14).
 *
 * WHY THIS EXISTS. The browser `RpcClient` opens an Effect span per call and
 * ships that span's `traceId` / `spanId` / `sampled` INSIDE the request
 * payload; `RpcServer` then uses those ids as the PARENT of
 * `${spanPrefix}.${tag}`. With no bridge, those ids come from Effect's native
 * tracer — a random trace id with `sampled` hard-coded to `true` — so every
 * RPC span and every `sql.execute` beneath it lands in a DIFFERENT trace from
 * `POST /api/rpc/…`, and the browser's 10% sampling decision is ignored by the
 * backend. Binding Effect's tracer to the page's existing `WebTracerProvider`
 * makes the payload ids the browser's real OTEL ids and its real decision, and
 * puts the `RpcClient.<tag>` span in the tree where it belongs.
 *
 * CONSENT. The provider is only registered after performance consent
 * (`signoz-provider.tsx`), and read here from
 * `browser-tracer-provider-registry.ts`, never from `otel-browser-config.ts`:
 * every RPC client imports this module, and that one loads zone.js.
 * `Layer.suspend` re-evaluates on every runtime build, so an unconsented user
 * silently gets Effect's native tracer exactly as today, which is why the
 * server must ALSO honour `traceparent` (the T1-fallback row of the Tier 1
 * suite).
 *
 * NO PROVIDER, NO PROPAGATION. When the page has no tracer provider (env
 * unset — production today — or no consent), Effect's NATIVE tracer still
 * opens an `RpcClient.<tag>` span per call, and `HttpClient` propagates that
 * span as `traceparent` + `b3` headers. Nothing ever exports the span, so
 * every server tree hung off a parent id that did not exist anywhere: three
 * roots per request, "This trace has missing spans" on every trace, and a
 * backend forced to sample at the browser's hard-coded `sampled: true`
 * (verified on prod trace `da607f5a36225773379ff5ac3ac82f3e`, 2026-09-10).
 * So the inert branch is not `Layer.empty` — it switches header propagation
 * OFF, and the Vercel request span becomes the honest root of the trace.
 * Effect's native spans still exist locally; they just stay local.
 *
 * This module ships in the browser bundle: no Node-only imports, ever.
 */
import * as Resource from "@effect/opentelemetry/Resource"
import * as Tracer from "@effect/opentelemetry/Tracer"
import { HttpClient } from "@effect/platform"
import { Effect, Layer } from "effect"
import { honourExternalSampling } from "./honour-external-sampling"
import { getTracerProvider } from "./browser-tracer-provider-registry"

/**
 * `NoTracePropagationLayer` when the page has no tracer provider, otherwise
 * Effect's tracer bound to that provider.
 *
 * `layerTracer` produces the `OtelTracer` TAG; `Tracer.make` turns it into an
 * Effect tracer, which `honourExternalSampling` wraps before `Layer.setTracer`
 * installs it. The output channel stays `never`, so this merges into any
 * client layer without widening its type.
 */
/** The inert branch: no OTEL provider on the page, so no trace headers leave it. */
export const NoTracePropagationLayer: Layer.Layer<never> = Layer.locallyScoped(
  HttpClient.currentTracerPropagation,
  false
)

export const EffectBrowserTracerLayer: Layer.Layer<never> = Layer.suspend(() => {
  const provider = getTracerProvider()
  if (!provider) return NoTracePropagationLayer
  return Layer.unwrapEffect(
    Effect.map(Tracer.make, (tracer) => Layer.setTracer(honourExternalSampling(tracer)))
  ).pipe(
    Layer.provide(Tracer.layerTracer),
    Layer.provide(Layer.succeed(Tracer.OtelTracerProvider, provider)),
    Layer.provide(Resource.layer({ serviceName: "cova-platform-web" }))
  )
})
